import { TextInput as NDSTextInput } from "@narmi/design_system";
import { ChangeEvent, InputHTMLAttributes, ReactNode } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

type TextInputTypes =
  | "text"
  | "tel"
  | "number"
  | "email"
  | "password"
  | "search"
  | "time"
  | "datetime-local";

interface TextInputProps<T extends FieldValues>
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    // value/defaultValue come from the react-hook-form Controller
    "type" | "value" | "defaultValue"
  > {
  control: Control<T, unknown>;
  name: Path<T>;
  label: string;
  type?: TextInputTypes;
  transform?: (value: string) => string;
  /** NDS content slot rendered inside the input box at its end (e.g. a character counter). */
  endContent?: ReactNode;
  /** External error shown when the field has no validation error (e.g. a failed server lookup). */
  error?: string;
}

function TextInput<T extends FieldValues>(props: TextInputProps<T>) {
  const { control, name, label, type, transform, error: externalError, ...inputProps } = props;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
        <NDSTextInput
          label={label}
          onChange={(value: string | ChangeEvent<HTMLInputElement>) => {
            const rawValue = typeof value === "string" ? value : value.target.value;
            const nextValue = transform ? transform(rawValue) : rawValue;
            onChange(nextValue);
          }}
          onBlur={onBlur}
          value={value ?? ""}
          error={error?.message ?? externalError}
          ref={ref}
          type={type ?? "text"}
          {...inputProps}
        />
      )}
    />
  );
}

export { TextInput };
